import React, { useState } from 'react';
import { Badge } from '../../../../components/badge';
import type { SurveySummaryQuestion } from '../../types/survey.types';
import ViewAllResponsesModal from './ViewAllResponsesModal';

interface EmailResponseProps {
    question: SurveySummaryQuestion;
    totalResponses: number;
}

const EmailResponse: React.FC<EmailResponseProps> = ({ 
    question, 
    totalResponses 
}) => {
    const { position, title, type, answered_count, options } = question;
    const [isViewAllModalOpen, setIsViewAllModalOpen] = useState(false);
    
    // Calculate response rate
    const responseRate = totalResponses > 0 ? ((answered_count / totalResponses) * 100).toFixed(1) : '0.0';
    
    // Sort options by count (descending) for better visual hierarchy
    const sortedOptions = [...options].sort((a, b) => b.count - a.count);
    
    // Limit displayed responses to 5 as shown in the image
    const displayedOptions = sortedOptions.slice(0, 5);
    
    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-600 p-6 shadow-sm">
            {/* Header Section */}
            <div className="flex items-start gap-3 mb-6">
                {/* Question Icon - Envelope */}
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 bg-[#F2FCFA] dark:bg-emerald-900/30">
                    <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18.9333 6.65283L11.4408 11.4253C11.1865 11.573 10.8977 11.6508 10.6037 11.6508C10.3097 11.6508 10.0209 11.573 9.7666 11.4253L2.2666 6.65283" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M17.2666 4.15283H3.93327C3.01279 4.15283 2.2666 4.89902 2.2666 5.8195V15.8195C2.2666 16.74 3.01279 17.4862 3.93327 17.4862H17.2666C18.1871 17.4862 18.9333 16.74 18.9333 15.8195V5.8195C18.9333 4.89902 18.1871 4.15283 17.2666 4.15283Z" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </div>
                
                <div className="flex-1">
                    {/* Question Title */}
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        {position}. {title}
                    </h3>
                    
                    {/* Question Type Badge and Response Info */}
                    <div className="flex items-center gap-4 flex-wrap">
                        <Badge 
                            variant="outline" 
                            className="text-[10px] text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-500 bg-white dark:bg-white"
                        >
                            {type.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                        </Badge>
                        
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                            <span className="font-medium">{answered_count}</span> of <span className="font-medium">{totalResponses}</span> responses
                        </div>
                        
                        <div className="text-sm font-medium text-[#004232] dark:text-[#00D6A8]">
                            {responseRate}% response rate
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Response List */}
            <div className="space-y-3">
                {displayedOptions.map((option, index) => (
                    <div 
                        key={option.option_id} 
                        className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    >
                        {/* Email Icon */}
                        <div className="flex-shrink-0">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M14 3H2C1.45 3 1 3.45 1 4V12C1 12.55 1.45 13 2 13H14C14.55 13 15 12.55 15 12V4C15 3.45 14.55 3 14 3Z" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M1 4L8 8.5L15 4" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>
                        
                        {/* Email Address */}
                        <p className="text-sm text-gray-900 dark:text-gray-100 flex-1">
                            {option.label}
                        </p>
                    </div>
                ))}
            </div>
            
            {/* No responses message */}
            {options.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No responses available for this question
                </div>
            ) : (
                /* Pagination Info */
                <div className="text-center mt-4 text-xs text-[#717182] dark:text-gray-400">
                    Showing {displayedOptions.length} of {options.length} responses - 
                    <button 
                        className="underline hover:no-underline cursor-pointer ml-1"
                        onClick={() => setIsViewAllModalOpen(true)}
                    >
                        View All
                    </button>
                </div>
            )}
            
            {/* View All Modal */}
            <ViewAllResponsesModal
                isOpen={isViewAllModalOpen}
                onClose={() => setIsViewAllModalOpen(false)}
                question={question}
                totalResponses={totalResponses}
            />
        </div>
    );
};

export default EmailResponse;
